import type { ListBlockContent } from "../types"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface ListBlockEditorProps {
  content: ListBlockContent | null
  onChange: (content: ListBlockContent) => void
}

export function ListBlockEditor({ content, onChange }: ListBlockEditorProps) {
  const c = content ?? {}
  const items = c.items ?? [""]
  const update = (partial: Partial<ListBlockContent>) =>
    onChange({ ...c, ...partial })
  const setItem = (i: number, val: string) => {
    const next = [...items]
    next[i] = val
    update({ items: next })
  }
  const addItem = () => update({ items: [...items, ""] })
  const removeItem = (i: number) =>
    update({ items: items.filter((_, j) => j !== i) })

  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-medium">List</h3>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Heading</Label>
          <Input
            value={c.heading ?? ""}
            onChange={(e) => update({ heading: e.target.value })}
            placeholder="Optional heading"
          />
        </div>
        <div>
          <Label>List Type</Label>
          <Select
            value={c.list_type ?? "unordered"}
            onValueChange={(v) => update({ list_type: v as "unordered" | "ordered" })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unordered">Bullet list</SelectItem>
              <SelectItem value="ordered">Numbered list</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Columns</Label>
          <Select
            value={String(c.columns ?? 1)}
            onValueChange={(v) => update({ columns: Number(v) as 1 | 2 })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1</SelectItem>
              <SelectItem value="2">2</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Items</Label>
          {items.map((item, i) => (
            <div key={i} className="flex gap-2">
              <Input
                value={item}
                onChange={(e) => setItem(i, e.target.value)}
                placeholder={`Item ${i + 1}`}
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)}>
                Remove
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addItem}>
            Add item
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
